'use client';

import { motion } from 'framer-motion';
import { ArrowRight, BookOpen } from 'lucide-react';
import { useTranslations } from 'next-intl';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { BlogVerticalStack } from '@/features/blog/components/blog-vertical-stack';
import { Link } from '@/i18n/routing';
import type { BlogPost } from '@/types/content';

interface BlogPreviewSectionProps {
  posts: BlogPost[];
}

export function BlogPreviewSection({ posts }: BlogPreviewSectionProps) {
  const t = useTranslations('landing.blog');

  return (
    <section
      id="blog"
      className="relative py-16 md:py-24 bg-muted/30 border-y border-border/50 overflow-hidden"
    >
      {/* Ambient gradient background blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-48 w-96 h-96 bg-primary/5 rounded-full blur-[128px]" />
        <div className="absolute bottom-1/4 -right-48 w-96 h-96 bg-blue-500/5 rounded-full blur-[128px]" />
      </div>

      {/* Header with container */}
      <div className="relative mb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.5 }}
          className="w-full text-center flex flex-col items-center"
        >
          <Badge
            variant="outline"
            className="mb-6 px-4 py-1.5 text-sm border-primary/20 bg-primary/5 text-primary"
          >
            <BookOpen className="w-3.5 h-3.5 mr-2" />
            {t('badge')}
          </Badge>

          <h2
            className="text-3xl md:text-5xl font-bold tracking-tight mb-6 text-foreground w-full"
            dangerouslySetInnerHTML={{
              __html: t
                .raw('title')
                .replace('<highlight>', '<span class="text-primary">')
                .replace('</highlight>', '</span>'),
            }}
          />

          <p className="text-lg text-muted-foreground max-w-2xl leading-relaxed">{t('subtitle')}</p>
        </motion.div>
      </div>

      {/* Blog posts vertical stack */}
      {posts.length > 0 ? (
        <div className="relative mb-12 max-w-4xl mx-auto px-4 md:px-6">
          <BlogVerticalStack posts={posts} />
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="container text-center py-12"
        >
          <p className="text-lg text-muted-foreground">{t('noPosts')}</p>
        </motion.div>
      )}

      {/* Button centered */}
      {posts.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="relative flex justify-center items-center w-full"
        >
          <Button asChild size="lg" className="group">
            <Link href="/blog">
              {t('viewAll')}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Button>
        </motion.div>
      )}
    </section>
  );
}
